import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Table, Badge, Modal } from 'react-bootstrap';
import axios from 'axios';

const PromotionManagement = () => {
  // Helper function to get default dates
  const getDefaultDates = () => {
    const now = new Date();
    const oneMonthLater = new Date();
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
    
    return {
      startDate: now.toISOString().slice(0, 16),
      endDate: oneMonthLater.toISOString().slice(0, 16)
    };
  };

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');

  // Promotions state
  const [promotions, setPromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [promotionsError, setPromotionsError] = useState('');
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const defaultDates = getDefaultDates();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'percentage',
    discountValue: 0,
    buyQuantity: 1,
    getQuantity: 1,
    specialPrice: 0,
    minOrderAmount: 0,
    maxDiscountAmount: 0,
    applicableCategories: [],
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    usageLimit: '',
    promoCode: '',
    color: '#ff6b6b',
    priority: 0
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      fetchMenus();
    }
  }, [profile]);

  useEffect(() => {
    if (selectedMenuId) {
      fetchPromotions();
      fetchCategories(selectedMenuId);
    }
  }, [selectedMenuId]);

  const fetchProfile = async () => {
    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      if (!token) {
        setProfileError('Токен не найден');
        setLoadingProfile(false);
        return;
      }

      const profileUrl = restaurantToken ? '/api/auth/profile' : '/api/manager/profile';
      
      const res = await axios.get(profileUrl, {
        headers: { 'x-auth-token': token }
      });
      setProfile(res.data);
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Ошибка загрузки профиля');
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchMenus = async () => {
    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      let restaurantId;
      
      if (restaurantToken) {
        restaurantId = profile._id;
      } else {
        const profileRes = await axios.get('/api/manager/profile', {
          headers: { 'x-auth-token': token }
        });
        restaurantId = profileRes.data.restaurantId;
      }
      
      const res = await axios.get(`/api/menu/${restaurantId}/list`, {
        headers: { 'x-auth-token': token }
      });
      setMenus(res.data);
      
      if (res.data.length > 0 && !selectedMenuId) {
        setSelectedMenuId(res.data[0]._id);
      }
    } catch (err) {
      console.error('Error fetching menus:', err);
    }
  };

  const fetchCategories = async (menuId) => {
    if (!menuId) {
      setCategories([]);
      return;
    }
    
    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      let restaurantId;
      
      if (restaurantToken) {
        restaurantId = profile._id;
      } else {
        const profileRes = await axios.get('/api/manager/profile', {
          headers: { 'x-auth-token': token }
        });
        restaurantId = profileRes.data.restaurantId;
      }
      
      const res = await axios.get(`/api/menu/by-id/${menuId}`, {
        headers: { 'x-auth-token': token }
      });
      
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  };

  const fetchPromotions = async () => {
    if (!selectedMenuId) return;
    
    try {
      setLoadingPromotions(true);
      setPromotionsError('');

      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      const res = await axios.get(`/api/promotions/menu/${selectedMenuId}`, {
        headers: { 'x-auth-token': token }
      });
      
      setPromotions(res.data);
    } catch (err) {
      setPromotionsError(err.response?.data?.message || 'Ошибка загрузки акций');
    } finally {
      setLoadingPromotions(false);
    }
  };

  const handleCreatePromotion = async () => {
    setEditingPromotion(null);
    
    // Загружаем текущие настройки доставки для меню
    let deliverySettings = {
      enabled: false,
      minOrderAmount: 0,
      deliveryFee: 0
    };
    
    // Получаем даты по умолчанию
    const defaultDates = getDefaultDates();
    
    if (selectedMenuId) {
      try {
        const managerToken = localStorage.getItem('managerToken');
        const restaurantToken = localStorage.getItem('restaurantToken');
        const token = managerToken || restaurantToken;
        
        const res = await axios.get(`/api/menu/by-id/${selectedMenuId}`, {
          headers: { 'x-auth-token': token }
        });
        
        const menu = res.data;
        deliverySettings = {
          enabled: menu.branding?.deliverySettings?.enabled || false,
          minOrderAmount: menu.branding?.deliverySettings?.minOrderAmount || 0,
          deliveryFee: menu.branding?.deliverySettings?.deliveryFee || 0
        };
      } catch (err) {
        console.error('Error fetching delivery settings:', err);
      }
    }
    
    setFormData({
      title: '',
      description: '',
      type: 'percentage',
      discountValue: 0,
      buyQuantity: 1,
      getQuantity: 1,
      specialPrice: 0,
      minOrderAmount: 0,
      maxDiscountAmount: 0,
      applicableCategories: [],
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      usageLimit: '',
      promoCode: '',
      color: '#ff6b6b',
      priority: 0,
      // Настройки доставки
      deliveryEnabled: formData.type === 'free_delivery' ? true : deliverySettings.enabled,
      minDeliveryAmount: deliverySettings.minOrderAmount,
      deliveryFee: deliverySettings.deliveryFee
    });
    setShowModal(true);
  };

  const handleEditPromotion = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      title: promotion.title,
      description: promotion.description || '',
      type: promotion.type,
      discountValue: promotion.discountValue || 0,
      buyQuantity: promotion.buyQuantity || 1,
      getQuantity: promotion.getQuantity || 1,
      specialPrice: promotion.specialPrice || 0,
      minOrderAmount: promotion.minOrderAmount || 0,
      maxDiscountAmount: promotion.maxDiscountAmount || 0,
      applicableCategories: promotion.applicableCategories || [],
      startDate: promotion.startDate ? new Date(promotion.startDate).toISOString().slice(0, 16) : '',
      endDate: promotion.endDate ? new Date(promotion.endDate).toISOString().slice(0, 16) : '',
      usageLimit: promotion.usageLimit || '',
      promoCode: promotion.promoCode || '',
      color: promotion.color || '#ff6b6b',
      priority: promotion.priority || 0,
      // Настройки доставки (для free_delivery)
      minDeliveryAmount: promotion.minDeliveryAmount ?? promotion.minOrderAmount ?? 0,
      deliveryFee: promotion.deliveryFee ?? 0
    });
    setShowModal(true);
  };

  const handleCategoryChange = (categoryName, isChecked) => {
    setFormData(prev => ({
      ...prev,
      applicableCategories: isChecked 
        ? [...prev.applicableCategories, categoryName]
        : prev.applicableCategories.filter(cat => cat !== categoryName)
    }));
  };

  const handleSavePromotion = async () => {
    try {
      // Валидация обязательных полей
      if (!formData.title || formData.title.trim() === '') {
        alert('Пожалуйста, укажите название акции');
        return;
      }

      if (!formData.startDate) {
        alert('Пожалуйста, укажите дату начала акции');
        return;
      }

      if (!formData.endDate) {
        alert('Пожалуйста, укажите дату окончания акции');
        return;
      }

      // Проверка что дата окончания позже даты начала
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate <= startDate) {
        alert('Дата окончания должна быть позже даты начала');
        return;
      }

      // Валидация в зависимости от типа акции
      if (formData.type === 'percentage' || formData.type === 'fixed_amount') {
        if (!formData.discountValue || formData.discountValue <= 0) {
          alert(`Пожалуйста, укажите ${formData.type === 'percentage' ? 'процент' : 'сумму'} скидки`);
          return;
        }
        
        if (formData.type === 'percentage' && formData.discountValue > 100) {
          alert('Процент скидки не может быть больше 100%');
          return;
        }
      }

      if (formData.type === 'buy_x_get_y') {
        if (!formData.buyQuantity || formData.buyQuantity <= 0) {
          alert('Пожалуйста, укажите количество для покупки');
          return;
        }
        
        if (!formData.getQuantity || formData.getQuantity <= 0) {
          alert('Пожалуйста, укажите количество для получения');
          return;
        }
      }

      if (formData.type === 'free_delivery') {
        if (!formData.minDeliveryAmount || formData.minDeliveryAmount < 0) {
          alert('Пожалуйста, укажите минимальную сумму заказа для бесплатной доставки');
          return;
        }
      }

      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;

      const data = {
        ...formData,
        menuId: selectedMenuId,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        promoCode: formData.promoCode === '' ? null : formData.promoCode,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null
      };

      if (editingPromotion) {
        await axios.put(`/api/promotions/${editingPromotion._id}`, data, {
          headers: { 'x-auth-token': token }
        });
      } else {
        await axios.post(`/api/promotions/menu/${selectedMenuId}`, data, {
          headers: { 'x-auth-token': token }
        });
      }

      // Если это акция типа "бесплатная доставка", автоматически включаем доставку в меню
      if (formData.type === 'free_delivery') {
        let restaurantId;
        
        if (restaurantToken) {
          restaurantId = profile._id;
        } else {
          const profileRes = await axios.get('/api/manager/profile', {
            headers: { 'x-auth-token': token }
          });
          restaurantId = profileRes.data.restaurantId;
        }
        
        await axios.put(`/api/menu/${restaurantId}/menus/${selectedMenuId}/branding`, {
          deliverySettings: {
            enabled: true,
            minOrderAmount: formData.minDeliveryAmount || 0,
            deliveryFee: formData.deliveryFee || 0
          }
        }, {
          headers: { 'x-auth-token': token }
        });
      }

      setShowModal(false);
      fetchPromotions();
    } catch (error) {
      console.error('Error saving promotion:', error);
      
      // Обработка различных типов ошибок
      if (error.response) {
        // Ошибка от сервера
        const errorMessage = error.response.data?.message || error.response.data?.error;
        
        if (error.response.status === 400) {
          alert(`Ошибка валидации: ${errorMessage || 'Проверьте правильность заполнения полей'}`);
        } else if (error.response.status === 401) {
          alert('Ошибка авторизации. Пожалуйста, войдите в систему снова');
        } else if (error.response.status === 403) {
          alert('У вас нет прав для создания/редактирования акций');
        } else if (error.response.status === 404) {
          alert('Меню не найдено');
        } else if (error.response.status === 500) {
          alert(`Ошибка сервера: ${errorMessage || 'Попробуйте позже'}`);
        } else {
          alert(`Ошибка при сохранении акции: ${errorMessage || 'Неизвестная ошибка'}`);
        }
      } else if (error.request) {
        // Запрос был отправлен, но ответа не получено
        alert('Ошибка соединения с сервером. Проверьте подключение к интернету');
      } else {
        // Ошибка при настройке запроса
        alert('Ошибка при отправке данных: ' + error.message);
      }
    }
  };

  const handleDeletePromotion = async (promotionId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту акцию?')) {
      return;
    }

    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;

      await axios.delete(`/api/promotions/${promotionId}`, {
        headers: { 'x-auth-token': token }
      });

      fetchPromotions();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      alert('Ошибка при удалении акции');
    }
  };

  const getPromotionTypeLabel = (type) => {
    switch (type) {
      case 'percentage': return 'Процентная скидка';
      case 'fixed_amount': return 'Фиксированная скидка';
      case 'buy_x_get_y': return 'Купи X получи Y';
      case 'free_delivery': return 'Бесплатная доставка';
      default: return type;
    }
  };

  const getPromotionStatus = (promotion) => {
    const now = new Date();
    const startDate = new Date(promotion.startDate);
    const endDate = new Date(promotion.endDate);

    if (!promotion.isActive) {
      return <Badge bg="secondary">Неактивна</Badge>;
    }

    if (now < startDate) {
      return <Badge bg="info">Скоро начнется</Badge>;
    }

    if (now > endDate) {
      return <Badge bg="danger">Завершена</Badge>;
    }

    if (promotion.usageLimit && promotion.usedCount >= promotion.usageLimit) {
      return <Badge bg="warning">Исчерпана</Badge>;
    }

    return <Badge bg="success">Активна</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  if (loadingProfile) {
    return <div className="mt-5 text-center">Загрузка профиля...</div>;
  }

  if (profileError) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Ошибка загрузки профиля</Alert.Heading>
          <p>{profileError}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="mb-4">
        <Col>
          <h1>Управление акциями</h1>
          <p className="mb-0">
            Ресторан: <strong>{profile?.restaurantName || '—'}</strong>
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Выберите меню</Form.Label>
            <Form.Select
              value={selectedMenuId}
              onChange={(e) => setSelectedMenuId(e.target.value)}
            >
              <option value="">Выберите меню</option>
              {menus.map((menu) => (
                <option key={menu._id} value={menu._id}>
                  {menu.title} — {menu.mode === 'dine_in' ? 'В зале' : 'Доставка'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <Button 
            variant="primary" 
            onClick={handleCreatePromotion}
            disabled={!selectedMenuId}
          >
            <i className="fas fa-plus me-2"></i>
            Создать акцию
          </Button>
        </Col>
      </Row>

      {selectedMenuId && (
        <>
          {/* Promotions Section */}
          <Row>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Акции меню</Card.Title>
                
                {promotionsError && <Alert variant="danger">{promotionsError}</Alert>}

                {loadingPromotions ? (
                  <div className="text-center py-3">
                    <div className="text-muted">Загрузка акций...</div>
                  </div>
                ) : promotions.length === 0 ? (
                  <div className="text-center py-3">
                    <h6 className="text-muted">Акции не найдены</h6>
                    <p className="text-muted small">
                      Создайте первую акцию для этого меню
                    </p>
                  </div>
                ) : (
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Название</th>
                        <th>Тип</th>
                        <th>Скидка</th>
                        <th>Период</th>
                        <th>Статус</th>
                        <th>Использовано</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promotions.map((promotion) => (
                        <tr key={promotion._id}>
                          <td>
                            <div>
                              <strong>{promotion.title}</strong>
                              {promotion.description && (
                                <div className="small text-muted">{promotion.description}</div>
                              )}
                            </div>
                          </td>
                          <td>{getPromotionTypeLabel(promotion.type)}</td>
                          <td>
                            {promotion.type === 'percentage' && `${promotion.discountValue}%`}
                            {promotion.type === 'fixed_amount' && `${promotion.discountValue} BYN`}
                            {promotion.type === 'buy_x_get_y' && promotion.description && (
                              promotion.specialPrice && promotion.specialPrice > 0 ? 
                                `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity} по ${promotion.specialPrice} BYN` :
                                `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity} бесплатно`
                            )}
                            {promotion.type === 'free_delivery' && 'Бесплатная доставка'}
                          </td>
                          <td>
                            <div className="small">
                              <div>С: {formatDate(promotion.startDate)}</div>
                              <div>По: {formatDate(promotion.endDate)}</div>
                            </div>
                          </td>
                          <td>{getPromotionStatus(promotion)}</td>
                          <td>
                            {promotion.usageLimit ? 
                              `${promotion.usedCount}/${promotion.usageLimit}` : 
                              `${promotion.usedCount}`
                            }
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditPromotion(promotion)}
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeletePromotion(promotion._id)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        </>
      )}

      {/* Promotion Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPromotion ? 'Редактировать акцию' : 'Создать акцию'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Название акции *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Введите название акции"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Тип акции *</Form.Label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({
                      ...formData, 
                      type: newType,
                      // Автоматически включаем доставку для типа "free_delivery"
                      deliveryEnabled: newType === 'free_delivery' ? true : formData.deliveryEnabled
                    });
                  }}
                >
                  <option value="percentage">Процентная скидка</option>
                  <option value="fixed_amount">Фиксированная скидка</option>
                  <option value="buy_x_get_y">Купи X получи Y</option>
                  <option value="free_delivery">Бесплатная доставка</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Описание</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Описание акции"
                />
              </Form.Group>
            </Col>

            {/* Delivery Settings - только для типа "бесплатная доставка" */}
            {formData.type === 'free_delivery' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Минимальная сумма заказа (BYN)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.minDeliveryAmount || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          minDeliveryAmount: parseFloat(e.target.value) || 0
                        })
                      }
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Стоимость доставки (BYN)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.deliveryFee || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          deliveryFee: parseFloat(e.target.value) || 0
                        })
                      }
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
            
            {(formData.type === 'percentage' || formData.type === 'fixed_amount') && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {formData.type === 'percentage' ? 'Размер скидки (%)' : 'Размер скидки (BYN)'} *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step={formData.type === 'percentage' ? '1' : '0.01'}
                    value={formData.discountValue}
                    onChange={(e) => setFormData({...formData, discountValue: parseFloat(e.target.value) || 0})}
                  />
                </Form.Group>
              </Col>
            )}
            
            {formData.type === 'buy_x_get_y' && (
              <>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Купить (количество) *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.buyQuantity}
                      onChange={(e) => setFormData({...formData, buyQuantity: parseInt(e.target.value) || 1})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Получить (количество) *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.getQuantity}
                      onChange={(e) => setFormData({...formData, getQuantity: parseInt(e.target.value) || 1})}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Специальная цена (BYN)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.specialPrice}
                      onChange={(e) => setFormData({...formData, specialPrice: parseFloat(e.target.value) || 0})}
                      placeholder="Оставьте 0 для бесплатной акции"
                    />
                    <Form.Text className="text-muted">
                      Укажите цену для акционных товаров (например, 2 BYN). Оставьте 0 для бесплатной акции.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </>
            )}
            
            {formData.type !== 'buy_x_get_y' && formData.type !== 'free_delivery' && (
              <>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Минимальная сумма заказа (BYN)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.minOrderAmount}
                      onChange={(e) => setFormData({...formData, minOrderAmount: parseFloat(e.target.value) || 0})}
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Максимальная скидка (BYN)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.maxDiscountAmount}
                      onChange={(e) => setFormData({...formData, maxDiscountAmount: parseFloat(e.target.value) || 0})}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Дата начала *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Дата окончания *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Лимит использований</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({...formData, usageLimit: e.target.value})}
                  placeholder="Оставьте пустым для безлимита"
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Промокод</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.promoCode}
                  onChange={(e) => setFormData({...formData, promoCode: e.target.value})}
                  placeholder="Оставьте пустым для автоматической активации"
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Цвет акции</Form.Label>
                <Form.Control
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                />
              </Form.Group>
            </Col>
            
            <Col md={12}>
              <Form.Group>
                <Form.Label>Применить к категориям</Form.Label>
                <Form.Text className="text-muted mb-2">
                  Выберите категории, к которым будет применяться акция. Если ничего не выбрано, акция будет применяться ко всем товарам.
                </Form.Text>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '10px' }}>
                  {categories.length === 0 ? (
                    <div className="text-muted">Категории не найдены</div>
                  ) : (
                    categories.map((category, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`category-${index}`}
                        label={category.name}
                        checked={formData.applicableCategories?.includes(category.name) || false}
                        onChange={(e) => handleCategoryChange(category.name, e.target.checked)}
                        className="mb-2"
                      />
                    ))
                  )}
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Приоритет</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSavePromotion}>
            {editingPromotion ? 'Сохранить' : 'Создать'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PromotionManagement;
